import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING,
  STUDY_KPI_KEYS_TO_NAMES_MAP,
  STUDY_KPI_KEYS_TO_TYPE_MAPPING
} from '@catalogues';
import {
  Kpi,
  KpiCategories,
  KpiConfig,
  KpiKeys,
  KpisByCategory,
  KpisByKey,
  Message,
  MilestoneName,
  ResponseState,
  Segment,
  StageName,
  StudyMilestones
} from '@models';
import {
  AppHeaderService,
  CommonFunctionsService,
  ExpandedPanelStateService,
  KpiCommonFunctionsService,
  KpiFilterService,
  KpiService,
  SelectedKpiService,
  SidebarService,
  SidePanelStateService,
  StudyInsightsService,
  StudyService
} from '@services';
import { catchError, distinctUntilChanged, finalize, forkJoin, map, Observable, of } from 'rxjs';

import { featureFlags } from '../../../environments/environment';

@Component({
  selector: 'study-details',
  templateUrl: './study-details.component.html',
  styleUrl: './study-details.component.scss'
})
export class StudyDetailsComponent implements OnInit, OnDestroy {
  private kpiCommonFunctionsService: KpiCommonFunctionsService = inject(KpiCommonFunctionsService);
  private studyService: StudyService = inject(StudyService);
  private kpiService: KpiService = inject(KpiService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private sidebarService: SidebarService = inject(SidebarService);
  private appHeaderService: AppHeaderService = inject(AppHeaderService);
  private router = inject(Router);

  private kpiFilterService: KpiFilterService = inject(KpiFilterService);
  private sidePanelStateService: SidePanelStateService = inject(SidePanelStateService);
  private expandedPanelStateService: ExpandedPanelStateService = inject(ExpandedPanelStateService);
  private selectedKpiService: SelectedKpiService = inject(SelectedKpiService);
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);
  private studyInsightsService: StudyInsightsService = inject(StudyInsightsService);
  private destroyRef: DestroyRef = inject(DestroyRef);

  pageTitle = 'Study --';
  protocolNumber?: string;
  studyMilestonesRes!: ResponseState<StudyMilestones>;
  filteredKpisByCategory: KpisByCategory | undefined = undefined;
  expandedGanttChartStages!: StageName[];
  allKpisByCategory!: KpisByCategory;
  studySk!: string;
  kpiConfig: KpiConfig = {};
  studySummary: Observable<string | Segment[]> = of('Loading...');

  sidebarCollapsed!: boolean;
  sidePanelExpanded!: boolean;
  isLoading = true;

  selectedKpiByKey?: { key: KpiKeys; kpi: Kpi };
  notAllowedError = false;
  browserTimezone = this.commonFunctionsService.getBrowserTimezone();
  keyMilestones: MilestoneName[] = [];

  async ngOnInit() {
    this.appHeaderService
      .getSponsorApplied()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Navigate to portfolio when sponsor changes
        this.router.navigate(['/portfolio']);
      });

    this.studySk = this.route.snapshot.paramMap.get('studySk') || '';

    // Subscribe to cached studies to check sponsor filter
    const cachedSubject = this.studyService.getCachedStudiesPortfolio();

    // Ensure studies data is loaded if not already cached
    // This handles direct navigation to study-details
    if (cachedSubject.value === null) {
      const parentClientNames = this.studyService.getParentClientNames();
      this.studyService.getStudiesPortfolio(parentClientNames).subscribe();
    }

    cachedSubject
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((studies) => {
        if (studies) {
          // Filter by studySk to get the current study
          const currentStudy = studies.find((study) => study.studySk === this.studySk);

          if (currentStudy) {
            // Get the selected sponsor from header
            const headerState = this.appHeaderService.getCurrentHeaderState();
            const sponsorSelected = headerState.selectedSponsor;

            // If sponsorSelected is not 'All' and doesn't match the study's clientName, navigate to portfolio
            if (sponsorSelected !== 'All' && currentStudy.clientName !== sponsorSelected) {
              this.router.navigate(['/portfolio']);
            }
          }
        }
      });

    const milestones$ = this.studyService.getMilestones(this.studySk).pipe(
      map((data) => ({ status: 'success', data }) as const),
      catchError((err: HttpErrorResponse) => {
        return of({ status: 'error', errorCode: err.status } as const);
      })
    );

    const kpiKeys: KpiKeys[] = Object.keys(STUDY_KPI_KEYS_TO_NAMES_MAP) as KpiKeys[];

    const kpiByCategoryResponses = this.getEmptyKpiByCategoryResponsesObject();
    const kpis$ = this.kpiService.getKpiAll(this.studySk, kpiKeys, this.browserTimezone).pipe(
      map((data) => {
        const kpisByKeyResponse = data as KpisByKey;
        Object.keys(STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING).forEach((categoryKey) => {
          const category = categoryKey as KpiCategories;
          STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING[category]?.forEach((kpiKey) => {
            if (Object.prototype.hasOwnProperty.call(kpisByKeyResponse, kpiKey)) {
              kpiByCategoryResponses[category]![kpiKey] = kpisByKeyResponse[kpiKey];
            }
          });
        });
        return { status: 'success', data: kpiByCategoryResponses } as const;
      }),
      catchError((err: HttpErrorResponse) => {
        this.allKpisByCategory = kpiByCategoryResponses;
        return of({ status: 'error', errorCode: err.status } as const);
      })
    );

    const kpiConfig$ = this.kpiService.getKpiConfig().pipe(
      map((data) => ({ status: 'success', data }) as const),
      catchError((err: HttpErrorResponse) => {
        return of({ status: 'error', errorCode: err.status } as const);
      })
    );

    this.joinEndpointCalls(milestones$, kpis$, kpiConfig$);

    this.sidebarService.sidebarCollapsed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((collapsed) => {
        this.sidebarCollapsed = collapsed;
      });

    this.sidePanelStateService.sidePanelExpanded$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.sidePanelStateService.setSidePanelExpanded(false);
        })
      )
      .subscribe((expanded) => {
        this.sidePanelExpanded = expanded;
      });

    this.selectedKpiService.selectedKpi$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.selectedKpiService.setSelectedKpi(undefined);
        })
      )
      .subscribe((key) => {
        this.selectedKpiChange(key);
      });
  }

  ngOnDestroy() {
    if (this.expandedPanelStateService.getExpandedPanelState()) {
      this.expandedPanelStateService.setExpandedPanelExpanded(false);
    }
  }

  getKeyMilestones(): MilestoneName[] {
    if (this.studyMilestonesRes && this.studyMilestonesRes.status !== 'success') return [];
    const keyMilestones: MilestoneName[] = [];
    this.studyMilestonesRes.data.stages
      .flatMap((stage) =>
        stage.milestones.flatMap((item) => (item.type === 'group' ? item.milestones : [item]))
      )
      .forEach((milestone) => {
        if (milestone.isKeyMilestone) {
          keyMilestones.push(milestone.name);
        }
      });

    return keyMilestones;
  }

  onExpandedStagesChange(stageNames: StageName[]) {
    Promise.resolve().then(() => {
      this.expandedGanttChartStages = stageNames;
      this.filteredKpisByCategory = this.kpiFilterService.filterKpisByStage(
        stageNames,
        this.allKpisByCategory
      );
    });
  }

  selectedKpiChange(kpiKey: KpiKeys | undefined) {
    if (!kpiKey) {
      this.onSidePanelClosed();
      return;
    }

    const clickedKpi: Kpi | undefined = this.getKpiByKey(kpiKey);
    if (!clickedKpi) return;
    const kpiValue = this.kpiCommonFunctionsService.getKpiDisplayValue(clickedKpi, kpiKey);
    if (kpiValue === undefined) {
      return;
    }

    this.selectedKpiByKey = { key: kpiKey, kpi: clickedKpi };
    if (!this.sidePanelExpanded) this.sidePanelStateService.setSidePanelExpanded(true);
  }

  onTermClicked(metadata: Record<string, any>) {
    const kpiKey = metadata?.['id'] as KpiKeys | undefined;
    if (!kpiKey) return;
    this.selectedKpiChange(kpiKey);
  }

  onSidePanelClosed(): void {
    this.selectedKpiByKey = undefined;
    this.sidePanelStateService.setSidePanelExpanded(false);
  }

  onExpandedPanelOpened() {
    this.expandedPanelStateService.setExpandedPanelExpanded(true);
  }

  private joinEndpointCalls(
    milestones$: Observable<ResponseState<StudyMilestones>>,
    kpis$: Observable<ResponseState<KpisByCategory>>,
    kpiConfig$: Observable<ResponseState<KpiConfig>>
  ) {
    forkJoin([milestones$, kpis$, kpiConfig$]).subscribe({
      next: ([milestones, kpis, kpiConfig]) => {
        if (milestones.status == 'success') {
          this.protocolNumber = milestones.data.studyNumber.protocolNumber;
          this.pageTitle = `Study ${this.protocolNumber}`;
        }

        if (kpis.status == 'success') this.allKpisByCategory = kpis.data;
        if (kpiConfig.status == 'success') this.kpiConfig = kpiConfig.data;

        this.checkNotAllowedError(milestones, kpis);
        this.studyMilestonesRes = milestones;
        this.keyMilestones = this.getKeyMilestones();

        if (featureFlags.enableStudyInsights && milestones.status == 'success') {
          this.studySummary = this.studyInsightsService
            .getStudyInsights(milestones.data, this.allKpisByCategory, 'studyinsightsv2')
            .pipe(
              map((insights) => {
                return this.mapResponseState(insights);
              })
            );
        }
        this.isLoading = false;
      }
    });
  }

  private mapResponseState(
    insights: { status: 'success'; data: Message[] } | { status: 'error'; errorCode: number }
  ) {
    if (insights.status === 'success') {
      return insights.data[1]?.content ?? 'No study insights available at this time.';
    }

    throw new Error(`Failed to load study insights (error code: ${insights.errorCode})`);
  }

  private checkNotAllowedError(...responses: ResponseState<any>[]): void {
    if (responses.some((r) => r.status === 'error' && r.errorCode === 401)) {
      this.notAllowedError = true;
    }
  }

  private getEmptyKpiByCategoryResponsesObject(): KpisByCategory {
    const emptyResponses: KpisByCategory = {} as KpisByCategory;
    (Object.keys(STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING) as KpiCategories[]).forEach(
      (categoryKey) => {
        emptyResponses[categoryKey] = {};

        (
          Object.values(STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING[categoryKey] || {}) as KpiKeys[]
        ).forEach((kpiKey) => {
          emptyResponses[categoryKey]![kpiKey] = this.getNoDataKpi(kpiKey);
        });
      }
    );

    return emptyResponses;
  }

  private getNoDataKpi(kpiKey: KpiKeys): Kpi {
    return {
      name: STUDY_KPI_KEYS_TO_NAMES_MAP[kpiKey]!,
      type: STUDY_KPI_KEYS_TO_TYPE_MAPPING[kpiKey]!,
      trendText: 'No trend data available',
      value: undefined,
      numerator: undefined,
      denominator: undefined,
      health: undefined
    };
  }
  private getKpiByKey(kpiKey: KpiKeys): Kpi | undefined {
    for (const kpisByKey of Object.values(this.allKpisByCategory) as KpisByKey[]) {
      for (const [key, value] of Object.entries(kpisByKey) as [KpiKeys, Kpi][]) {
        if (key === kpiKey) {
          return value;
        }
      }
    }
    return;
  }
}
